import { Card, Chip, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { Button, IconButton, Icons, InformationModal } from 'utoniq-ui'

import { OfficialInformationConsoleBoxFragment } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { DateTime } from '@/core/entities'

type OfficialInformationConsoleBoxProps = {
  officialInformation: OfficialInformationConsoleBoxFragment
  onDelete: (officialInformationId: string) => Promise<void>
}

export const OfficialInformationConsoleBox: Organism<OfficialInformationConsoleBoxProps> =
  ({ officialInformation, onDelete }) => {
    const { t } = useTranslation('consoleOfficialInformations')
    const { showMessage, showError } = useMessage()
    const { router, generatePath, routerPages } = useRouter()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState<boolean>(false)
    const [deletingOfficialInformation, setDeletingOfficialInformation] =
      useState<boolean>(false)

    const options = [
      {
        label: t('edit', '編集'),
        onClick() {
          router.push(
            generatePath(routerPages.ConsoleOfficialInformationEdit, {
              officialInformationId: officialInformation.id,
            })
          )
        },
      },
      {
        label: t('delete', '削除'),
        onClick() {
          setAnchorEl(null)
          setOpenDeleteConfirm(true)
        },
      },
    ]

    const handleDelete = async () => {
      setDeletingOfficialInformation(true)
      await onDelete(officialInformation.id)
        .then(() => {
          setOpenDeleteConfirm(false)
          showMessage(t('deleteOfficialInformationSuccess'), 'success')
        })
        .catch(showError)
      setDeletingOfficialInformation(false)
    }

    return (
      <Card elevation={0} sx={{ bgcolor: 'transparent' }}>
        <Stack direction="row">
          <Stack spacing={2} flex={1}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <OfficialInformationTag status={officialInformation.status} />
              {officialInformation.status !== 'scheduled' && (
                <Typography variant="caption" color="text.secondary">
                  {t('publishedAt', {
                    date: DateTime.formatDate(officialInformation.publishedAt),
                  })}
                </Typography>
              )}
            </Stack>
            <Typography variant="h6" sx={styleSets.lineCamp(2)}>
              {officialInformation.title}
            </Typography>
            {officialInformation.status === 'scheduled' ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Icons.Calendar
                  sx={{ width: 12, height: 12, color: 'primary.main' }}
                />
                <Typography variant="overline" color="primary.main">
                  {t('postedAtInfo', {
                    date: DateTime.formatDate(
                      officialInformation.publishedAt,
                      'LLAHH:mm'
                    ),
                  })}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('createdAt', {
                  date: DateTime.formatDate(officialInformation.createdAt),
                })}
              </Typography>
            )}
          </Stack>
          <Stack>
            <IconButton.More
              onClick={event => setAnchorEl(event.currentTarget)}
              size="xSmall"
              bgcolor="grey.50"
              color="grey.700"
              sx={{ transform: 'rotate(90deg)' }}
            />
            <Menu
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {options.map(({ label, ...option }) => (
                <MenuItem {...option} sx={{ width: 200 }} key={label}>
                  {label}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Stack>
        <InformationModal
          open={openDeleteConfirm}
          onClose={() => setOpenDeleteConfirm(false)}
          title={t('deleteOfficialInformationTitle')}
          content={t('deleteOfficialInformationContent')}
          buttons={
            <Button
              size="large"
              sx={{ width: 212 }}
              loading={deletingOfficialInformation}
              disabled={deletingOfficialInformation}
              onClick={handleDelete}
            >
              {t('deleteBtn')}
            </Button>
          }
          cancelText={t('close')}
          sx={{
            // 単に props で指定するだけだと
            // default のスタイルが優先されるため
            // セレクタを用いた CSS 指定を行っている
            '& .MuiBackdrop-root': {
              // 画面幅に依らずバックドロップを画面全体にかける
              maxWidth: '100%',
            },
          }}
        />
      </Card>
    )
  }

type OfficialInformationTagProps = {
  status: OfficialInformationConsoleBoxFragment['status']
}
export const OfficialInformationTag: FC<OfficialInformationTagProps> = ({
  status,
}) => {
  const { t } = useTranslation('consoleOfficialInformations')

  const { bgColor, color } = useMemo(() => {
    if (status === 'scheduled') {
      return { bgColor: 'secondary.main', color: 'white' }
    }
    return { bgColor: 'primary.main', color: 'white' }
  }, [status])

  return (
    <Chip
      sx={{
        bgcolor: bgColor,
        height: 20,
        '.MuiChip-label': { px: 1 },
      }}
      label={
        <Typography sx={{ display: 'block' }} color={color} variant="overline">
          {t(`${status}Status`)}
        </Typography>
      }
    />
  )
}
